import { useState } from "react";
import { useLingui } from "@lingui/react/macro";
import { Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AppDialog } from "@/components/common/AppDialog";

/** Longest category name the API accepts. */
export const CATEGORY_NAME_MAX = 100;

interface AddCategoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called with the trimmed name. The caller closes the dialog once it has saved. */
  onSave: (name: string) => void;
  isPending?: boolean;
  /** Why the last save failed (for instance, the name is taken), shown by the box. */
  error?: string | null;
}

/**
 * Adds a category to the concept the Menu Items list is showing. The concept is
 * not asked for here: categories belong to the one on screen.
 */
export function AddCategoryDialog(props: AddCategoryDialogProps) {
  // Remounted on open so a cancelled name does not come back.
  return <AddCategoryForm key={props.open ? "open" : "closed"} {...props} />;
}

function AddCategoryForm({
  open,
  onOpenChange,
  onSave,
  isPending = false,
  error = null,
}: AddCategoryDialogProps) {
  const { t } = useLingui();
  const [name, setName] = useState("");
  const [validation, setValidation] = useState<string | null>(null);

  const submit = () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setValidation(t`Category name is required`);
      return;
    }
    if (trimmed.length > CATEGORY_NAME_MAX) {
      setValidation(t`Category name must be ${CATEGORY_NAME_MAX} characters or fewer`);
      return;
    }
    setValidation(null);
    onSave(trimmed);
  };

  const message = validation ?? error;

  return (
    <AppDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t`Add category`}
      description={t`Create a category to group menu items in this concept.`}
      footer={
        <div className="flex w-full justify-end gap-3">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t`Cancel`}
          </Button>
          <Button onClick={submit} disabled={isPending}>
            {t`Save`}
          </Button>
        </div>
      }
    >
      <div className="mb-4 flex items-start gap-2 rounded-md bg-(--color-surface-muted) px-3 py-2.5 text-xs text-(--color-text-secondary)">
        <Info className="mt-0.5 size-3.5 shrink-0" />
        {t`Categories are set up per concept and show above the menu items list.`}
      </div>

      <form
        className="space-y-2"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <Label htmlFor="category-name">{t`Category name`}</Label>
        <Input
          id="category-name"
          autoFocus
          placeholder={t`Enter category name...`}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        {message && <p className="text-xs text-(--color-danger-text)">{message}</p>}
      </form>
    </AppDialog>
  );
}
